import { action, getParent, types } from "@gadgetinc/mobx-quick-tree";
import { encryptApplicationSecret } from "../../appSecrets";
import { GadgetDomainModelClass, contentHash, getAppSettings } from "../../utils";

/**
 * See packages/state-trees/src/connections/ShopifyConnectionConfig.ts for documentation
 * on the different types of Shopify configurations
 */
export const ShopifyBaseAppConfig = <Name extends string>(name: Name) => {
  class BasicAppConfig extends GadgetDomainModelClass(name, {
    apiKey: types.string,
    apiSecretKey: types.string,
    appId: types.maybe(types.string),
    appHandle: types.maybe(types.string),
    appName: types.maybe(types.string),
    embedded: types.maybe(types.boolean),
    shopifyManagedScopes: types.optional(types.array(types.string), []),
  }) {
    get contentHash() {
      return contentHash([this.apiKey, this.apiSecretKey, this.appId, this.appHandle, this.appName, this.embedded]);
    }
    get shopifyManagedInstallation() {
      return this.shopifyManagedScopes.length > 0;
    }

    @action
    remove(): boolean {
      const parentConfig = getParent(this, 2);
      return !!parentConfig.removeAppConfig(this.key);
    }
    @action
    setApiKey(value: string) {
      this.apiKey = value;
    }
    @action
    setApiSecretKey(value: string) {
      const publicKey = getAppSettings(this).secretsPublicKey;
      const secret = encryptApplicationSecret(value, publicKey);

      this.apiSecretKey = secret;
    }
    @action
    setShopifyManagedScopes(value: string[]) {
      this.shopifyManagedScopes.replace(value);
    }
    @action
    clearApiSecretKey() {
      this.apiSecretKey = "";
    }
    @action
    setAppId(value?: string) {
      this.appId = value;
    }
    @action
    setAppHandle(value?: string) {
      this.appHandle = value;
    }
    @action
    setEmbedded(value?: boolean) {
      this.embedded = value;
    }

    @action
    setAppName(value?: string) {
      this.appName = value;
    }

    @action
    setMetadata(value: ShopifyAppMetadata) {
      this.setAppId(value.id);
      this.setAppHandle(value.handle);
      this.setAppName(value.title);
      this.setEmbedded(value.embedded);
      this.setShopifyManagedScopes(value.shopifyManagedScopes);
    }
  }
  return BasicAppConfig;
};

/** Descriptor object for metadata returned from the Shopify API about an app */
export interface ShopifyAppMetadata {
  id: string;
  handle: string;
  title: string;
  embedded: boolean;
  shopifyManagedScopes: string[];
}
